import type { Routes } from '@angular/router';
import { roleAccessGuard } from './core/role-access.guard';

export const appRoutes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: 'tableau-de-bord' },
  {
    // Pour tous les profils — la cloche du rail est son entrée, le contenu se borne au rôle.
    path: 'notifications',
    title: 'Notifications — UniIT',
    loadComponent: () =>
      import('./features/shared/components/notifications.page').then((m) => m.NotificationsPage),
  },
  {
    path: 'tableau-de-bord',
    title: 'Tableau de bord — UniIT',
    canMatch: [roleAccessGuard],
    loadComponent: () =>
      import('./features/dashboard/dashboard.page').then((m) => m.DashboardPage),
  },
  {
    path: 'equipements',
    title: 'Parc matériel — UniIT',
    canMatch: [roleAccessGuard],
    loadComponent: () =>
      import('./features/inventory/components/asset-list.page').then((m) => m.AssetListPage),
  },
  {
    path: 'mon-materiel',
    title: 'Mon matériel — UniIT',
    canMatch: [roleAccessGuard],
    loadComponent: () =>
      import('./features/inventory/components/my-equipment.page').then((m) => m.MyEquipmentPage),
  },
  {
    // Stock de consommables (SPEC-PIN-001, UC-17 à UC-21). Même feature que le parc : les deux
    // moitiés du référentiel relèvent de la même spec, seul le mode de gestion les sépare.
    path: 'consommables',
    title: 'Stock de consommables — UniIT',
    canMatch: [roleAccessGuard],
    loadComponent: () =>
      import('./features/inventory/components/consumable-list.page').then(
        (m) => m.ConsumableListPage,
      ),
  },
  {
    // Fiche collaborateur (SPEC-PIN-008 §6, UC-03). Déclarée **avant** « employes » : le garde
    // reçoit les segments restants de l'URL, donc la route « employes » verrait
    // `/employes/<id>` comme un chemin hors menu et redirigerait avant que cette route ne soit
    // essayée. Sans entrée de menu propre, la fiche dépend du périmètre de « Vos employés » :
    // d'où `navPath`, lu par le garde.
    path: 'employes/:id',
    title: 'Fiche collaborateur — UniIT',
    data: { navPath: '/employes' },
    canMatch: [roleAccessGuard],
    loadComponent: () =>
      import('./features/directory/components/collaborator-detail.page').then(
        (m) => m.CollaboratorDetailPage,
      ),
  },
  {
    path: 'employes',
    title: 'Collaborateurs — UniIT',
    canMatch: [roleAccessGuard],
    loadComponent: () =>
      import('./features/directory/components/collaborator-list.page').then(
        (m) => m.CollaboratorListPage,
      ),
  },
  {
    path: 'flux',
    title: 'Arrivées & départs — UniIT',
    canMatch: [roleAccessGuard],
    loadComponent: () =>
      import('./features/onboarding/components/flow-list.page').then((m) => m.FlowListPage),
  },
  {
    // Gestion des packs : Administrateur parc IT uniquement (SPEC-PIN-002 §6). Le garde s'appuie
    // sur la navigation du rôle, où l'entrée n'existe que pour lui.
    path: 'flux/packs',
    title: 'Packs de dotation — UniIT',
    canMatch: [roleAccessGuard],
    loadComponent: () =>
      import('./features/onboarding/components/pack-list.page').then((m) => m.PackListPage),
  },
  // Demandes d'achat (SPEC-PIN-007). Les routes les plus spécifiques d'abord : le garde reçoit les
  // segments restants de l'URL, et « demandes/:id » avalerait « demandes/nouvelle » comme un
  // identifiant. Les écrans de travail (file de qualification, file comptable) ont leur propre
  // entrée de menu ; le formulaire, la qualification d'une demande et le détail dépendent du
  // périmètre de « Mes demandes d'achat », d'où `navPath`, lu par le garde.
  {
    path: 'demandes/nouvelle',
    title: "Nouvelle demande d'achat — UniIT",
    data: { navPath: '/demandes' },
    canMatch: [roleAccessGuard],
    loadComponent: () =>
      import('./features/purchasing/components/request-form.page').then((m) => m.RequestFormPage),
  },
  {
    path: 'demandes/qualification',
    title: 'File de qualification IT — UniIT',
    canMatch: [roleAccessGuard],
    loadComponent: () =>
      import('./features/purchasing/components/qualification-queue.page').then(
        (m) => m.QualificationQueuePage,
      ),
  },
  {
    path: 'demandes/approbation',
    title: "File d'approbation comptable — UniIT",
    canMatch: [roleAccessGuard],
    loadComponent: () =>
      import('./features/purchasing/components/approval-queue.page').then(
        (m) => m.ApprovalQueuePage,
      ),
  },
  {
    path: 'demandes/:id/edition',
    title: "Demande d'achat — UniIT",
    data: { navPath: '/demandes' },
    canMatch: [roleAccessGuard],
    loadComponent: () =>
      import('./features/purchasing/components/request-form.page').then((m) => m.RequestFormPage),
  },
  {
    path: 'demandes/:id/qualification',
    title: "Qualification d'une demande — UniIT",
    data: { navPath: '/demandes/qualification' },
    canMatch: [roleAccessGuard],
    loadComponent: () =>
      import('./features/purchasing/components/request-qualification.page').then(
        (m) => m.RequestQualificationPage,
      ),
  },
  {
    path: 'demandes/:id',
    title: "Demande d'achat — UniIT",
    data: { navPath: '/demandes' },
    canMatch: [roleAccessGuard],
    loadComponent: () =>
      import('./features/purchasing/components/request-detail.page').then(
        (m) => m.RequestDetailPage,
      ),
  },
  {
    path: 'demandes',
    title: "Mes demandes d'achat — UniIT",
    canMatch: [roleAccessGuard],
    loadComponent: () =>
      import('./features/purchasing/components/request-list.page').then((m) => m.RequestListPage),
  },
  {
    path: 'commandes',
    title: 'Achats & commandes — UniIT',
    canMatch: [roleAccessGuard],
    loadComponent: () =>
      import('./features/purchasing/components/order-list.page').then((m) => m.OrderListPage),
  },
  {
    // Catalogue et panier : Administrateur parc IT et Gestionnaire IT (SPEC-PIN-003 §3).
    path: 'commandes/catalogue',
    title: 'Catalogue interne — UniIT',
    canMatch: [roleAccessGuard],
    loadComponent: () =>
      import('./features/purchasing/components/catalog.page').then((m) => m.CatalogPage),
  },
  // Support technique & SAV (SPEC-PIN-005). Les routes les plus spécifiques d'abord, pour la même
  // raison que les demandes d'achat : « support/:id » avalerait « support/nouveau ». Le formulaire et
  // le détail dépendent du périmètre de l'entrée « Support », d'où `navPath`, lu par le garde.
  {
    path: 'support/nouveau',
    title: 'Déclarer un incident — UniIT',
    data: { navPath: '/support' },
    canMatch: [roleAccessGuard],
    loadComponent: () =>
      import('./features/support/components/ticket-form.page').then((m) => m.TicketFormPage),
  },
  {
    path: 'support/:id',
    title: 'Ticket de support — UniIT',
    data: { navPath: '/support' },
    canMatch: [roleAccessGuard],
    loadComponent: () =>
      import('./features/support/components/ticket-detail.page').then((m) => m.TicketDetailPage),
  },
  {
    path: 'support',
    title: 'Support — UniIT',
    canMatch: [roleAccessGuard],
    loadComponent: () =>
      import('./features/support/components/ticket-list.page').then((m) => m.TicketListPage),
  },
  // Écrans maquettés mais hors périmètre V1 : ils affichent la spec qui les porte et la version
  // où ils sont attendus, plutôt qu'une interface non branchée.
  {
    path: ':section',
    canMatch: [roleAccessGuard],
    loadComponent: () => import('./features/shared/coming-soon.page').then((m) => m.ComingSoonPage),
  },
  { path: '**', redirectTo: 'tableau-de-bord' },
];
